import React from 'react';
import T from 'prop-types';
import cx from 'classnames';

import AppIcon from '../../../../components/AppIcon';
import CompanyAppsDropdown from '../../../company-page-v2/components/CompanyPerformance/CrossAppUsage/CompanyAppsDropdown';

const renderOptionLabel = (app, { size = 24, className } = {}) => (
  <div className={cx('company-apps-dropdown-label', className)}>
    <AppIcon
      alt={app.nickname}
      url={app.icon_url}
      store={app.store}
      size={size}
      hideHtmlTitle
      withBorder
      containerClasses="company-apps-dropdown-label__icon"
    />
    <span className="company-apps-dropdown-label__name">{app.nickname}</span>
  </div>
);

const subscriptionFooter = (
  <a
    href="https://apptopia.com/en/ai-dashboard-requests"
    target="_blank"
    rel="noopener noreferrer"
    className="company-apps-dropdown-footer__link"
  >
    All apps available with subscription
  </a>
);

const makeAppsOptions = (apps = [], isPublic = false) => {
  const visibleApps = isPublic ? apps.filter((app) => app.finance_sector === null) : apps;
  return visibleApps.map((app) => ({
    value: {
      unified_ref_no: app.unified_ref_no,
      store: app.store,
    },
    label: renderOptionLabel(app),
    query: app.nickname,
    buttonLabel: renderOptionLabel(app, {
      size: 16,
      className: 'company-apps-dropdown-label--button',
    }),
    sector: app.finance_sector?.name || '',
  }));
};

const AppsDropdown = ({
  selected,
  financeApps,
  isPublic = false,
  onChange,
  onAppsFiltersChange,
}) => {
  const { data, settings, filters } = financeApps;

  const appsPagination = {
    limit: filters.limit,
    offset: filters.offset,
    total: data.total_count || 0,
  };

  const handleSearchChange = (query) => {
    onAppsFiltersChange({ query, offset: 0 });
  };

  const handleLoadMore = () => {
    onAppsFiltersChange({ offset: filters.offset + filters.limit });
  };

  const handleChange = (value, name) => {
    onChange(value);
    if (isPublic && window.heap) {
      window.heap.track('AI App Impact Selected', {
        app_unified_ref_no: value.unified_ref_no,
        app_name: name,
      });
    }
  };

  if (isPublic) {
    return (
      <CompanyAppsDropdown
        selected={selected}
        options={makeAppsOptions(data.finance_apps, isPublic)}
        idKey="unified_ref_no"
        hideSearch
        footer={subscriptionFooter}
        onChange={handleChange}
      />
    );
  }

  return (
    <CompanyAppsDropdown
      selected={selected}
      options={makeAppsOptions(data.finance_apps, isPublic)}
      groupAppsBy="sector"
      idKey="unified_ref_no"
      manualSearch
      pending={settings.pending}
      pagination={appsPagination}
      onSearchChange={handleSearchChange}
      onLoadMore={handleLoadMore}
      onChange={handleChange}
    />
  );
};

AppsDropdown.propTypes = {
  selected: T.shape({
    unified_ref_no: T.string,
    store: T.string,
  }),
  financeApps: T.shape({
    data: T.shape({
      finance_apps: T.arrayOf(
        T.shape({
          unified_ref_no: T.string,
          store: T.string,
          nickname: T.string,
          icon_url: T.string,
          finance_sector: T.shape({
            name: T.string,
          }),
        })
      ),
      total_count: T.number,
    }),
    settings: T.shape({
      pending: T.bool,
    }),
    filters: T.shape({
      limit: T.number,
      offset: T.number,
    }),
  }).isRequired,
  isPublic: T.bool,
  onChange: T.func.isRequired,
  onAppsFiltersChange: T.func.isRequired,
};

export default AppsDropdown;
